import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  TextField,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Grid,
  Avatar,
  Divider,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Checkbox
} from '@mui/material';
import { 
  ShoppingCart,
  Payment,
  CheckCircle,
  ArrowBack,
  LocalShipping
} from '@mui/icons-material';
import { useParams, Link } from 'react-router-dom';
import { eventService } from '../../services/eventService';
import { createProductPurchase } from '../../services/productPurchaseService';
import LegalModal from '../../components/LegalModal';
import { legalService, purchaseAcceptedFromTemplates, type LegalAcceptedItem } from '../../services/legalService';
import PixCopyCodeField from './components/PixCopyCodeField';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { phoneValidation, applyPhoneMask } from '../../utils/phoneValidation';
import { CHECKOUT_PUBLIC_ERROR_MESSAGE } from '../../utils/checkoutPublicError';

// Mercado Pago Integration
import paymentServiceMercadoPago from '../../services/mercadoPagoService';
import PaymentBrick from './components/PaymentBrick';

interface EventData {
  _id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  location: string;
  address: string;
  city: string;
  state: string;
  coverImage?: string;
  parishName?: string;
  products?: ProductData[];
}

interface ProductData {
  _id: string;
  name: string;
  description: string;
  price: number;
  quantity: number;
  image?: string;
  startSaleDate?: string;
  endSaleDate?: string;
}

// Interface específica para o formulário de compra
interface PurchaseFormFields {
  name: string;
  email: string;
  phone: string;
  quantity: number;
  agreeTerms: boolean;
}

// Schema de validação
const purchaseSchema: yup.ObjectSchema<PurchaseFormFields> = yup.object({
  name: yup
    .string()
    .required('Nome é obrigatório')
    .min(2, 'Nome deve ter pelo menos 2 caracteres')
    .max(100, 'Nome deve ter no máximo 100 caracteres')
    .matches(
      /^[a-zA-ZÀ-ÿ\s'-]+$/,
      'Nome deve conter apenas letras, espaços, hífens e apóstrofos'
    )
    .test('no-numbers', 'Nome não pode conter números', (value) => {
      if (!value) return true;
      return !/\d/.test(value);
    })
    .test('min-words', 'Nome deve conter pelo menos um nome completo', (value) => {
      if (!value) return true;
      const words = value.trim().split(/\s+/).filter(word => word.length > 0);
      return words.length >= 1 && words.every(word => /^[a-zA-ZÀ-ÿ'-]+$/.test(word));
    }),
  email: yup
    .string()
    .required('Email é obrigatório')
    .email('Email inválido'),
  phone: yup
    .string()
    .required('Telefone é obrigatório')
    .test('phone-validation', 'Telefone inválido', phoneValidation('Telefone inválido')),
  quantity: yup
    .number()
    .required('Quantidade é obrigatória')
    .min(1, 'Quantidade deve ser pelo menos 1')
    .max(100, 'Quantidade máxima é 100'),
  agreeTerms: yup
    .boolean()
    .required('Você deve aceitar os termos e condições')
    .oneOf([true], 'Você deve aceitar os termos e condições')
});

const steps = ['Informações Pessoais', 'Revisão', 'Pagamento', 'Confirmação'];

const ProductPurchasePage: React.FC = () => {
  const { eventSlug, productId } = useParams<{ eventSlug: string; productId: string }>();
  
  const [event, setEvent] = useState<EventData | null>(null);
  const [product, setProduct] = useState<ProductData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  // Estados para pagamento
  const [submitting, setSubmitting] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [pixPaymentPending, setPixPaymentPending] = useState(false);
  const [pixQRCodeBase64, setPixQRCodeBase64] = useState<string>('');
  const [pixCode, setPixCode] = useState<string>('');
  const [purchaseId, setPurchaseId] = useState<string>('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalModalType, setLegalModalType] = useState<'terms' | 'privacy' | 'refund'>('terms');
  const [legalAccepted, setLegalAccepted] = useState<LegalAcceptedItem[]>([]);

  // React Hook Form
  const methods = useForm<PurchaseFormFields>({
    resolver: yupResolver(purchaseSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      quantity: 1,
      agreeTerms: false
    }
  });

  const { control, handleSubmit, watch, formState: { errors, isValid } } = methods;
  const watchedQuantity = watch('quantity');

  useEffect(() => {
    legalService
      .listTemplates()
      .then((templates) => setLegalAccepted(purchaseAcceptedFromTemplates(templates)))
      .catch(() => undefined);
  }, []);

  // Buscar dados do evento e produto
  useEffect(() => {
    const fetchEventAndProduct = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!eventSlug || !productId) {
          setError('Parâmetros inválidos.');
          return;
        }

        const response = await eventService.getEventBySlug(eventSlug);

        if (response.success && response.data) {
          const eventData = response.data;
          setEvent(eventData);

          // Encontrar o produto específico
          const foundProduct = eventData.products?.find((p: ProductData) => p._id === productId);
          if (foundProduct) {
            setProduct(foundProduct);
            // Limpar erros do formulário quando produto é carregado
            methods.clearErrors();
            methods.reset(methods.getValues());
          } else {
            setError('Produto não encontrado.');
          }
        } else {
          setError('Não foi possível carregar os dados do evento');
        }
      } catch (err) {
        console.error('Erro ao buscar evento/produto:', err);
        setError('Erro ao carregar dados. Por favor, tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchEventAndProduct();
  }, [eventSlug, productId, methods]);

  const handleNext = async () => {
    if (activeStep === 0) {
      // Validar formulário antes de prosseguir
      const isValid = await methods.trigger();
      if (!isValid) {
        setError('Por favor, corrija os erros no formulário antes de continuar.');
        return;
      }
    }
    
    setError(null);
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleMercadoPagoPayment = async (
    cardTokenId: string, 
    paymentMethodId: string, 
    issuerId: string | null, 
    installmentsValue: number,
    payer?: {
      email?: string;
      first_name?: string;
      last_name?: string;
      identification?: {
        type: string;
        number: string;
      };
    },
    installmentTotalAmount?: number
  ) => {
    if (!event || !product) {
      setError('Erro: Evento ou Produto não encontrado.');
      setSubmitting(false);
      return;
    }

    const formData = methods.getValues();
    const totalAmount = product.price * formData.quantity;
    const finalTransactionAmount =
      typeof installmentTotalAmount === 'number' && installmentTotalAmount > totalAmount
        ? installmentTotalAmount
        : totalAmount;

    setPaymentProcessing(true);
    setPaymentError(null);
    setError(null);

    try {
      const response = await paymentServiceMercadoPago.processPayment({
        productId: product._id,
        eventId: event._id,
        quantity: formData.quantity,
        customerEmail: formData.email,
        customerName: formData.name,
        customerCPF: payer?.identification?.number || '',
        cardToken: cardTokenId,
        paymentMethodId: paymentMethodId,
        issuerId: issuerId || undefined,
        installments: installmentsValue,
        transactionAmount: finalTransactionAmount,
        external_reference: purchaseId,
      });

      console.log('🟢 Resposta do pagamento:', response);

      if (response.success && response.data?.status === 'approved') {
        setSuccess(true);
        setActiveStep(3);
        return;
      }

      if (response.success && response.data?.status === 'in_process') {
        // Pagamento em processamento - aguardar confirmação
        setPaymentProcessing(false);
        return;
      }

      if (response.success && response.data?.payment_method_id === 'pix') {
        const transactionData = response.data.point_of_interaction?.transaction_data;
        if (transactionData?.qr_code_base64 && transactionData.qr_code) {
          setPixQRCodeBase64(transactionData.qr_code_base64);
          setPixCode(transactionData.qr_code);
          setPixPaymentPending(true);
          return;
        }
      }

      if (response.success && response.data) {
        setSuccess(true);
        setActiveStep(3);
      } else {
        setPaymentError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
        setError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
      }
    } catch {
      setPaymentError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
      setError(CHECKOUT_PUBLIC_ERROR_MESSAGE);
    } finally {
      setPaymentProcessing(false);
      setSubmitting(false);
    }
  };

  const onSubmit = async (data: PurchaseFormFields) => {
    try {
      setSubmitting(true);
      setError(null);
      setPaymentError(null);

      if (!event || !product) {
        setError('Evento ou produto não encontrado.');
        return;
      }
      if (!legalAccepted.length) {
        setError('Não foi possível carregar os documentos legais. Recarregue a página.');
        return;
      }

      // Criar a compra no backend
      const purchaseRequestData = {
        eventId: event._id,
        productId: product._id,
        customerName: data.name,
        customerEmail: data.email,
        customerPhone: data.phone,
        quantity: data.quantity,
        legalAccepted,
      };

      const purchaseResponse = await createProductPurchase(purchaseRequestData);
      
      if (purchaseResponse.success) {
        setPurchaseId(purchaseResponse.data._id);
        
        // Se o produto é gratuito, finalizar diretamente
        if (product.price === 0) {
          setSuccess(true);
          setActiveStep(3);
          return;
        }

        // Se precisa de pagamento, mostrar etapa de pagamento
        setActiveStep(2);
      } else {
        setError(purchaseResponse.message || 'Erro ao criar compra.');
      }
    } catch (error: any) {
      console.error('Erro ao processar compra:', error);
      setError(error.message || 'Erro ao processar compra. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <FormProvider {...methods}>
            <Box component="form" onSubmit={handleSubmit(onSubmit)}>
              <Typography variant="h6" gutterBottom>
                Informações Pessoais
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Nome Completo *"
                        placeholder="Nome Completo *"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Email *"
                        type="email"
                        placeholder="Email*"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Telefone *"
                        placeholder="(11) 98765-4321"
                        error={!!errors.phone}
                        helperText={errors.phone?.message || "Ex: (11) 98765-4321 para celular ou (11) 3456-7890 para fixo"}
                        required
                        onChange={(e) => {
                          const formatted = applyPhoneMask(e.target.value);
                          field.onChange(formatted);
                        }}
                        inputProps={{
                          maxLength: 15, // (11) 98765-4321
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="quantity"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Quantidade *"
                        type="number"
                        placeholder="Quantidade *"
                        inputProps={{ 
                          min: 1, 
                          max: product?.quantity || 1 
                        }}
                        error={!!errors.quantity}
                        helperText={errors.quantity?.message || `Máximo disponível: ${product?.quantity || 1}`}
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="agreeTerms"
                    control={control}
                    render={({ field }) => (
                      <FormControl error={!!errors.agreeTerms}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...field}
                              checked={field.value}
                              required
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Concordo com os{' '}
                              <Box
                                component="span"
                                sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setLegalModalType('terms');
                                  setLegalModalOpen(true);
                                }}
                              >
                                Termos de Uso
                              </Box>
                              ,{' '}
                              <Box
                                component="span"
                                sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setLegalModalType('privacy');
                                  setLegalModalOpen(true);
                                }}
                              >
                                Privacidade
                              </Box>
                              {' '}e{' '}
                              <Box
                                component="span"
                                sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setLegalModalType('refund');
                                  setLegalModalOpen(true);
                                }}
                              >
                                Reembolso
                              </Box>
                            </Typography>
                          }
                        />
                        {errors.agreeTerms && (
                          <FormHelperText>{errors.agreeTerms.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Revisão da Compra
            </Typography>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {product?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {product?.description}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Typography variant="h6" color="primary">
                    R$ {product?.price.toFixed(2).replace('.', ',')}
                  </Typography>
                  <Chip label={`${watchedQuantity}x`} color="primary" />
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    R$ {((product?.price || 0) * watchedQuantity).toFixed(2).replace('.', ',')}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
            <Typography variant="body2" color="text.secondary">
              * Produto disponível para retirada no local do evento
            </Typography>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Pagamento
            </Typography>
            
            {paymentError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {paymentError}
              </Alert>
            )}

            {pixPaymentPending ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Pagamento PIX Pendente
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Escaneie o QR Code abaixo ou copie o código PIX para finalizar o pagamento:
                </Typography>
                {pixQRCodeBase64 && (
                  <Box sx={{ mb: 3 }}>
                    <img 
                      src={`data:image/png;base64,${pixQRCodeBase64}`} 
                      alt="QR Code PIX"
                      style={{ maxWidth: '200px', height: 'auto' }}
                    />
                  </Box>
                )}
                {pixCode && (
                  <PixCopyCodeField code={pixCode} label="Código PIX" />
                )}
                <Button 
                  variant="outlined"
                  onClick={async () => {
                    try {
                      // Verifica o status do pagamento uma vez
                      const response = await fetch(`/api/product-purchases/check-status/${purchaseId}`, {
                        method: 'GET',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                      });
                      
                      const result = await response.json();
                      
                      console.log('Status do pagamento:', result.data);
                      
                      // Verificar diferentes status possíveis
                      const paymentStatus = result.data.paymentStatus;
                      const status = result.data.status;
                      
                      // Só considera como pago se ambos os status estiverem confirmados
                      if (result.success && paymentStatus === 'paid' && (status === 'confirmed' || status === 'approved')) {
                        setPaymentSuccess(true);
                        setPixPaymentPending(false);
                        setActiveStep(3);
                        setPaymentError(null); // Limpar erros anteriores
                      } else if (paymentStatus === 'paid' && status === 'pending') {
                        setPaymentError('Pagamento foi processado pelo Mercado Pago, aguardando confirmação final. Tente novamente em alguns instantes.');
                      } else if (paymentStatus === 'pending') {
                        setPaymentError('Pagamento PIX gerado, aguardando confirmação. Verifique se você realizou o pagamento e tente novamente em alguns instantes.');
                      } else if (paymentStatus === 'rejected' || status === 'rejected' || paymentStatus === 'cancelled' || status === 'cancelled') {
                        setPaymentError('Pagamento foi rejeitado ou cancelado. Tente realizar um novo pagamento.');
                      } else {
                        setPaymentError(`Status do pagamento: ${paymentStatus}, Status geral: ${status}. Aguarde a confirmação ou tente novamente.`);
                      }
                    } catch (error) {
                      console.error('Erro ao verificar pagamento:', error);
                      setPaymentError('Erro ao verificar status do pagamento.');
                    }
                  }}
                >
                  Verificar Pagamento
                </Button>
              </Box>
            ) : (
              <PaymentBrick
                amount={product ? product.price * watchedQuantity : 0}
                payerEmail={watch('email')}
                onCardTokenReceived={handleMercadoPagoPayment}
                onError={(error) => {
                  console.error('Erro no PaymentBrick:', error);
                  setPaymentError('Erro ao carregar formulário de pagamento');
                }}
                onSuccess={() => {
                  console.log('Pagamento aprovado via PaymentBrick');
                }}
              />
            )}
          </Box>
        );
      default:
        return 'Etapa desconhecida';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Carregando...
        </Typography>
      </Container>
    );
  }

  if (error || !event || !product) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Produto não encontrado'}
        </Alert>
        <Button 
          component={Link} 
          to={`/${eventSlug}`}
          startIcon={<ArrowBack />}
        >
          Voltar ao Evento
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button 
          component={Link} 
          to={`/${eventSlug}`}
          startIcon={<ArrowBack />}
          sx={{ mb: 2 }}
        >
          Voltar ao Evento
        </Button>
        
        <Typography variant="h4" component="h1" gutterBottom>
          Comprar Produto
        </Typography>
        
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="h6" gutterBottom>
                  {event.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {format(new Date(event.startDate), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {event.location}, {event.city}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {product.image ? (
                    <Avatar 
                      src={product.image} 
                      sx={{ width: 60, height: 60 }}
                    />
                  ) : (
                    <Avatar sx={{ width: 60, height: 60, bgcolor: 'primary.main' }}>
                      <ShoppingCart />
                    </Avatar>
                  )}
                  <Box>
                    <Typography variant="h6">{product.name}</Typography>
                    <Typography variant="h6" color="primary">
                      R$ {product.price.toFixed(2).replace('.', ',')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Alertas de erro */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Conteúdo da etapa */}
      <Box sx={{ mb: 4 }}>
        {getStepContent(activeStep)}
      </Box>

      {/* Botões de navegação */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
        >
          Voltar
        </Button>
        
        <Box>
          {activeStep === 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <Payment />}
            >
              {submitting ? 'Processando...' : 'Finalizar Compra'}
            </Button>
          ) : activeStep === 2 ? (
            <Button
              variant="contained"
              disabled={paymentProcessing}
              startIcon={paymentProcessing ? <CircularProgress size={20} /> : <Payment />}
            >
              {paymentProcessing ? 'Processando Pagamento...' : 'Processar Pagamento'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={activeStep === 0 && !isValid}
            >
              Próximo
            </Button>
          )}
        </Box>
      </Box>

      {/* Etapa de sucesso */}
      {activeStep === 3 && success && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Compra Realizada com Sucesso!
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Você receberá um email de confirmação em breve.
          </Typography>
          
          {purchaseId && (
            <Card sx={{ mb: 3, p: 2, maxWidth: 400, mx: 'auto' }}>
              <Typography variant="h6" gutterBottom>
                Detalhes da Compra
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Produto:</strong> {product?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Quantidade:</strong> {watchedQuantity}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Valor Total:</strong> R$ {((product?.price || 0) * watchedQuantity).toFixed(2).replace('.', ',')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Código de Confirmação:</strong> {purchaseId}
              </Typography>
            </Card>
          )}
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Produto disponível para retirada no local do evento.
          </Typography>
          <Button 
            component={Link} 
            to={`/${eventSlug}`}
            variant="contained"
            startIcon={<LocalShipping />}
          >
            Voltar ao Evento
          </Button>
        </Box>
      )}

      {/* Mensagem de sucesso quando pagamento for verificado */}
      {activeStep === 3 && paymentSuccess && !success && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Pagamento Confirmado!
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Seu pagamento foi processado com sucesso. Você receberá um email de confirmação em breve.
          </Typography>
          
          <Button 
            component={Link} 
            to={`/${eventSlug}`}
            variant="contained"
            startIcon={<LocalShipping />}
            sx={{ mt: 2 }}
          >
            Voltar ao Evento
          </Button>
        </Box>
      )}
      <LegalModal open={legalModalOpen} onClose={() => setLegalModalOpen(false)} type={legalModalType} />
    </Container>
  );
};

export default ProductPurchasePage; 